import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
} from '@mui/material';
import { ShoppingCart as CartIcon, Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';

import { productAPI } from '../services/api';
import { setProducts, setLoading, setError } from '../store/slices/productSlice';
import Loading from '../components/common/Loading';

const ProductCard = ({ product }) => {
  const navigate = useNavigate();

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'transform 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 3,
        }
      }}
    >
      <CardMedia
        component="img"
        height="200"
        image={product.image || 'https://via.placeholder.com/200x200?text=No+Image'}
        alt={product.name}
        sx={{ objectFit: 'cover' }}
      />
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography gutterBottom variant="h6" component="h2">
          {product.name}
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {product.description.substring(0, 100)}
          {product.description.length > 100 ? '...' : ''}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" color="primary" sx={{ mr: 1 }}>
            ${product.price.toFixed(2)}
          </Typography>
          <Chip
            label={product.category.name}
            size="small"
            color="secondary"
            variant="outlined"
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Stock: {product.stock}
          </Typography>
          {product.rating && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Rating: {product.rating.toFixed(1)}/5
            </Typography>
          )}
        </Box>
      </CardContent>
      <CardActions>
        <Button
          fullWidth
          variant="contained"
          startIcon={<CartIcon />}
          onClick={() => navigate(`/products/${product._id}`)}
        >
          View Details
        </Button>
      </CardActions>
    </Card>
  );
};

const CategoryCard = ({ category, onClick }) => {
  return (
    <Paper
      sx={{
        p: 2,
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'transform 0.2s',
        '&:hover': {
          bgcolor: 'action.hover',
          transform: 'translateY(-4px)',
          boxShadow: 2,
        },
      }}
      onClick={() => onClick(category._id)}
    >
      <Typography variant="h6">{category.name}</Typography>
      <Typography variant="body2" color="text.secondary">
        {category.productsCount || ''} {category.productsCount ? 'Products' : ''}
      </Typography>
    </Paper>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { products, loading, error } = useSelector((state) => state.products);
  const [categories, setCategories] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
  });

  // Load products and extract categories
  useEffect(() => {
    loadProducts();
  }, []);

  // Extract unique categories when products change
  useEffect(() => {
    if (products.length > 0) {
      const uniqueCategories = [];
      const categoryMap = new Map();
      
      products.forEach(product => {
        const categoryId = product.category._id;
        const categoryName = product.category.name;
        
        if (!categoryMap.has(categoryId)) {
          categoryMap.set(categoryId, true);
          uniqueCategories.push({
            _id: categoryId,
            name: categoryName
          });
        }
      });
      
      setCategories(uniqueCategories);
    }
  }, [products]);

  const loadProducts = async () => {
    try {
      dispatch(setLoading(true));
      const response = await productAPI.getAll();
      if (Array.isArray(response.data)) {
        const transformedProducts = response.data.map(product => ({
          ...product,
          category: {
            _id: product.category,
            name: product.category
          }
        }));
        dispatch(setProducts(transformedProducts));
      } else if (response.data && response.data.data) {
        const transformedProducts = response.data.data.map(product => ({
          ...product,
          category: {
            _id: product.category,
            name: product.category
          }
        }));
        dispatch(setProducts(transformedProducts));
      } else {
        dispatch(setError('Invalid response format'));
        toast.error('Failed to load products');
      }
    } catch (error) {
      dispatch(setError(error.message));
      toast.error('Failed to load products');
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCategoryClick = (categoryId) => {
    setFilters(prev => ({
      ...prev,
      category: categoryId
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      category: '',
    });
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(filters.search.toLowerCase());
    const matchesCategory = !filters.category || product.category._id === filters.category;
    return matchesSearch && matchesCategory;
  });

  if (loading) {
    return <Loading message="Loading products..." />;
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography color="error" variant="h6">
          {error}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Hero Section */}
        <Box
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            p: 8,
            borderRadius: 2,
            mb: 6,
            textAlign: 'center',
          }}
        >
          <Typography variant="h3" component="h1" gutterBottom>
            Welcome to Our Store
          </Typography>
          <Typography variant="h6" sx={{ mb: 4 }}>
            Discover amazing products at great prices
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            onClick={() => navigate('/products')}
          >
            Shop Now
          </Button>
        </Box>

        {/* Quick Search and Filter */}
        <Paper sx={{ p: 2, mb: 4 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                placeholder="Search products..."
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex' }}>
                <FormControl fullWidth sx={{ mr: 1 }}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    name="category"
                    value={filters.category}
                    onChange={handleFilterChange}
                    label="Category"
                  >
                    <MenuItem value="">All Categories</MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category._id} value={category._id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button 
                  variant="outlined" 
                  onClick={handleClearFilters}
                  sx={{ height: 56 }}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Categories Section */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            Shop by Category
          </Typography>
          <Grid container spacing={2}>
            {categories.map((category) => (
              <Grid item xs={6} sm={4} md={3} key={category._id}>
                <CategoryCard 
                  category={category} 
                  onClick={handleCategoryClick}
                />
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ my: 4 }} />

        {/* All Products Section */}
        <Box>
          <Typography variant="h4" component="h2" gutterBottom>
            {filters.category 
              ? `Products in ${categories.find(c => c._id === filters.category)?.name || 'Category'}`
              : filters.search
                ? 'Search Results'
                : 'All Products'
            }
          </Typography>
          {filteredProducts.length === 0 ? (
            <Typography variant="h6" color="text.secondary" align="center" sx={{ my: 4 }}>
              No products found matching your filters
            </Typography>
          ) : (
            <Grid container spacing={4}>
              {filteredProducts.map((product) => (
                <Grid item xs={12} sm={6} md={3} key={product._id}>
                  <ProductCard product={product} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default Home; 